import { Component, Input, forwardRef, Output, EventEmitter, SimpleChanges, ElementRef } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'
import moment from 'moment'
import 'moment-timezone'
import { GeoActions } from '../../actions/geo-actions'
import { Subscription } from 'rxjs'
import { TimeZone } from '../../types'
import { NuclearObservable } from '../../nuclear-observable'
import { geoGetters } from '../../stores/geo-store'

@Component({
  selector: 'datetime-picker',
  styles: [`
    :host { display: block; }
  `],
  template: `
    <pop-card id="datetime-picker" class="small-bottom-buffer-lg" [customStyle]="{'padding': '10px', 'max-width': '300px'}">
      <div class="text-center">
        <ngb-datepicker
          [ngModel]="dateValue"
          (ngModelChange)="dateValue = $event"
          [displayMonths]="1"
          navigation="arrows"
          [showWeekNumbers]="false"
          [firstDayOfWeek]="7"
          [minDate]="dateStructFromDate(minDate)"
          [maxDate]="dateStructFromDate(maxDate)"></ngb-datepicker>
      </div>

      <div class="d-flex align-items-center">
        <timepicker
          [ngModel]="timeValue"
          (ngModelChange)="timeValue = $event"
          [showMinutes]="false"></timepicker>
        <span class="small-left-buffer">
          <select *ngIf="timeZones" class="form-control" [(ngModel)]="timeZoneValue">
            <option disabled="disabled" [value]="null" selected>Select a timezone</option>
            <option *ngFor="let tz of timeZones" [value]="tz.id">{{tz.name}}</option>
          </select>
        </span>
        <!--<span class="small-left-buffer fas fa-times"></span>-->
      </div>
      <div [popover]="saveDeadlineTooltipText" triggers="mouseenter">
        <button class="btn btn-secondary btn-block" (click)="onChoose()" [disabled]="checkDeadline()">Save Deadline</button>
      </div>
      <hr>
      <div class="d-flex justify-content-start">
        <span class="fas fa-question-circle text-secondary tiny-right-buffer"></span>
        <span class="small text-muted">Remember to build in extra time to review and revise before your final deadline. <a target="_blank" [href]="helpPageLink">More tips</a></span>
      </div>
      <div
        class="cursor-pointer small-top-buffer small-padding bg-gray text-center text-danger"
        style="margin-right: -10px; margin-left: -10px; margin-bottom: -10px;"
        (click)="onRemove()">
        <span>Remove Deadline</span>
      </div>
    </pop-card>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimePickerComponent),
      multi: true
    }
  ]
})
export class DatetimePickerComponent implements ControlValueAccessor {
  @Input('timeZone') timeZoneInput: string;
  @Input('datetime') datetimeInput: Date;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Output() choose: EventEmitter<{ date: Date, timeZone: string }> = new EventEmitter<{ date: Date, timeZone: string }>();
  @Output() remove: EventEmitter<null> = new EventEmitter<null>();
  @Output() cancel: EventEmitter<null> = new EventEmitter<null>();

  timeZones: { id: string, name: string }[] = [
    { id: 'America/New_York', name: 'New York' },
    { id: 'America/Chicago', name: 'Chicago' },
    { id: 'America/Denver', name: 'Denver' },
    { id: 'America/Los_Angeles', name: 'Los Angeles' }
  ];

  private _dateValue: NgbDateStruct;
  private _timeValue: Date = new Date();
  private _timeZoneValue: string;

  private DEFAULT_TIMEZONE: string = 'America/Denver'; // Used if tz not supplied and moment can't guess

  private tzObs: Subscription;


  propagateChange = (_: any) => { };

  constructor(
    private _elementRef: ElementRef,
    private geoActions: GeoActions
  ) { }

  ngOnInit(): void {
    this.parseInputValues()

    this.geoActions.loadTimeZones()
    const tzObs = NuclearObservable.from<TimeZone[]>(geoGetters.timeZones, { toJS: true }).subscribe(tzs => this.timeZones = tzs ? tzs : this.timeZones)
  }

  parseInputValues(): void {
    this.timeZoneValue = this.timeZoneInput || moment.tz.guess() || this.DEFAULT_TIMEZONE
    const { dateValue, timeValue } = this.getWorkingValuesFromInput(this.datetimeInput)
    this.dateValue = dateValue || this.dateStructFromDate(this.minDate)
    this.timeValue = timeValue
  }

  ngOnValueChanges(changes: SimpleChanges): void {
    const datetime = changes.datetime.currentValue
    if (datetime) {
      this.writeValue(datetime)
    }
  }

  get dateValue(): NgbDateStruct {
    return this._dateValue
  }

  set dateValue(val: NgbDateStruct) {
    this._dateValue = val
    const newDateTime = this.dateTimeValueToDate(this.dateValue, this.timeValue, this.timeZoneValue)
    this.propagateChange(newDateTime)
  }

  get timeValue(): Date {
    return this._timeValue
  }

  set timeValue(val: Date) {
    this._timeValue = val
    const newDateTime = this.dateTimeValueToDate(this.dateValue, this.timeValue, this.timeZoneValue)
    this.propagateChange(newDateTime)
  }

  get timeZoneValue(): string {
    return this._timeZoneValue
  }

  set timeZoneValue(val: string) {
    this._timeZoneValue = val
    const newDateTime = this.dateTimeValueToDate(this.dateValue, this.timeValue, this.timeZoneValue)
    this.propagateChange(newDateTime)
  }

  get saveDeadlineTooltipText(): string {
    if(this.checkDeadline()) {
      return `To save your deadline, please choose any time at or later than ${this.formattedMinDeadline}`
    } else {
      return ''
    }
  }

  get formattedMinDeadline(): string {
    return moment.tz(this.minDate, this.timeZoneInput).format('ha (z) MM/DD/YY')
  }

  dateTimeValueToDate(dateValue: NgbDateStruct, timeValue: Date, timeZoneValue: string): Date {
    if (dateValue && timeValue && timeZoneValue) {
      const year = dateValue.year
      const month = dateValue.month - 1
      const dayOfMonth = dateValue.day
      const hours = timeValue.getHours()
      const formattedFromTz = moment.tz([year, month, dayOfMonth, hours], timeZoneValue).toDate()
      return formattedFromTz
    }
  }

  dateStructFromDate(date: Date): NgbDateStruct {
    if (date) {
      return {
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear()
      }
    }
  }


  // Not used yet
  onCancel(): void {
    this.cancel.emit()
  }

  onChoose(): void {
    this.choose.emit({ date: this.dateTimeValueToDate(this.dateValue, this.timeValue, this.timeZoneValue), timeZone: this.timeZoneValue })
  }

  onRemove(): void {
    this.remove.emit()
  }

  // ControlValueAccessor Interface // formControlName="example"
  writeValue(datetimeInput: Date): void {
    if (datetimeInput) {
      const { dateValue, timeValue } = this.getWorkingValuesFromInput(datetimeInput)
      this.dateValue = dateValue
      this.timeValue = timeValue
    }
  }

  getWorkingValuesFromInput(datetimeInput: Date): {
    dateValue: NgbDateStruct,
    timeValue: Date
  } {
    const timeValue = this.timeValueFromTimeAndZone(datetimeInput, this.timeZoneValue)
    const dateValue = this.dateStructFromDate(timeValue)
    return {
      dateValue,
      timeValue
    }
  }

  timeValueFromTimeAndZone(time: Date, zone: string): Date {
    if (time && zone) {
      const inTz = moment(time).tz(this.timeZoneValue)
      return new Date(inTz.year(), inTz.month(), inTz.date(), inTz.hours())
    }
  }

  registerOnChange(fn): void {
    this.propagateChange = fn
  }

  registerOnTouched(): void {
    // No need to do anything here
  }

  checkDeadline(): boolean {
    if (this.dateValue && this.timeValue && this.timeZoneValue) {
      const year = this.dateValue.year
      const month = this.dateValue.month - 1
      const dayOfMonth = this.dateValue.day
      const hours = this.timeValue.getHours()

      const formattedFromTz = moment.tz([year, month, dayOfMonth, hours], this.timeZoneValue).toDate()
      if (formattedFromTz >= moment(this.minDate).subtract(1, 'h').toDate()) {
        return false
      } else {
        return true
      }
    } else {
      return true
    }
  }

  onDestroy(): void {
    this.tzObs.unsubscribe()
  }

  public helpPageLink: string = "https://help.verblio.com/my-account/making-requests/setting-deadlines-for-requests";
}
